import { useState, useRef, useEffect } from "react";
import { Send, Mic, MicOff, Sparkles, Loader2, RefreshCw, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { adminAgentChat, transcribeAudio } from "@/lib/api";

const SUGGESTIONS = [
  "Son 7 günün analitik özeti nedir?",
  "Aktif kampanyaları listele",
  "Bugün gelen leadleri göster",
  "Bilgi tabanındaki başlıkları listele",
  "Bu haftanın günlük özetini hazırla",
];

export default function AIAgentTab() {
  const [messages, setMessages] = useState(() => [
    { role: "assistant", content: "Merhaba 👋 Ben size yardımcı olacak yönetim asistanınızım. Doğal dilde sorabilir veya yazabilirsiniz: kampanya/fiyat/lead listele, ekle, sil, analiz özeti çıkar, bilgi tabanı ekle, site tara." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [recording, setRecording] = useState(false);
  const scrollRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const r = await adminAgentChat({ session_id: sessionId, message: text });
      if (!sessionId) setSessionId(r.session_id);
      setMessages((m) => [...m, { role: "assistant", content: r.reply, actions: r.actions || [] }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI asistan yanıtı alınamadı");
      setMessages((m) => [...m, { role: "assistant", content: "(Hata: yanıt alınamadı)" }]);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setMessages([{ role: "assistant", content: "Yeni oturum başlatıldı. Nasıl yardımcı olabilirim?" }]);
    setSessionId(null);
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setBusy(true);
        try {
          const r = await transcribeAudio(blob, "tr");
          if (r.text && r.text.trim()) await send(r.text);
          else toast.error("Ses anlaşılmadı");
        } catch { toast.error("Ses tanıma hatası"); }
        finally { setBusy(false); }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error("Mikrofon erişimi reddedildi");
    }
  };

  const stopRec = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop();
    setRecording(false);
  };

  return (
    <div className="space-y-4" data-testid="ai-agent-tab">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-display flex items-center gap-2">
              <Sparkles className="w-4 h-4" style={{ color: "#B39B7A" }} /> Yönetim AI Asistanı
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Doğal dilde komut ver: liste/ekle/sil/özet çıkar. Sesle veya yazarak konuşabilirsiniz.
            </p>
          </div>
          <Button data-testid="ai-agent-reset" size="sm" variant="outline" onClick={reset} disabled={busy}>
            <RefreshCw className="w-3 h-3 mr-1.5" /> Yeni oturum
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Suggestions */}
          {messages.length <= 1 && (
            <div className="flex flex-wrap gap-1.5">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  data-testid={`agent-sugg-${i}`}
                  onClick={() => send(s)}
                  disabled={busy}
                  className="text-[11px] px-3 py-1.5 rounded-full bg-secondary/60 hover:bg-secondary border border-border/40 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} className="border border-border/40 rounded-2xl bg-white/40 p-4 space-y-3 h-[480px] overflow-y-auto chat-scroll">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] space-y-2 ${m.role === "user" ? "" : ""}`}>
                  <div
                    className={`px-4 py-2.5 text-sm whitespace-pre-wrap rounded-2xl ${
                      m.role === "user"
                        ? "rounded-br-md text-white"
                        : "rounded-bl-md bg-secondary text-foreground"
                    }`}
                    style={m.role === "user" ? { background: "#B39B7A" } : {}}
                  >
                    {m.content}
                  </div>
                  {/* Action chips */}
                  {m.actions && m.actions.length > 0 && (
                    <div className="space-y-1">
                      {m.actions.map((a, j) => (
                        <details key={j} className="bg-white/70 border border-border/40 rounded-xl text-xs">
                          <summary className="cursor-pointer px-3 py-2 flex items-center gap-2 hover:bg-secondary/30 rounded-xl">
                            <ChevronDown className="w-3 h-3" />
                            <code className="font-mono text-[10px]">{a.action}</code>
                            {a.ok ? <Badge variant="secondary" className="text-[10px] py-0 px-1.5">✓</Badge>
                                  : <Badge variant="destructive" className="text-[10px] py-0 px-1.5">✗</Badge>}
                            {a.result?.count !== undefined && (
                              <span className="text-muted-foreground">{a.result.count} kayıt</span>
                            )}
                          </summary>
                          <pre className="px-3 py-2 text-[10px] leading-relaxed overflow-x-auto bg-secondary/20 rounded-b-xl whitespace-pre-wrap break-all">
                            {JSON.stringify(a.result || a.error || a, null, 2).slice(0, 1500)}
                          </pre>
                        </details>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-secondary rounded-2xl rounded-bl-md px-4 py-2.5 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                  Düşünüyor...
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="flex items-center gap-2">
            <Button
              data-testid="agent-mic"
              size="icon"
              onClick={recording ? stopRec : startRec}
              disabled={busy}
              className={`h-10 w-10 rounded-full ${recording ? "bg-red-500 hover:bg-red-600" : ""}`}
              style={!recording ? { background: "#8B9B88" } : {}}
            >
              {recording ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </Button>
            <Input
              data-testid="agent-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Örn: 'Bu hafta kaç lead geldi?' veya 'Yaz Kampanyasını sil'"
              disabled={busy || recording}
              className="bg-white"
            />
            <Button
              data-testid="agent-send"
              size="icon"
              onClick={() => send()}
              disabled={busy || !input.trim()}
              className="h-10 w-10 rounded-full"
              style={{ background: "#B39B7A" }}
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
